import { useLocation } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { cn } from "@/lib/utils"
import {
  CURRENT_ENVIRONMENT,
  environmentUrl,
  getBackendBuild,
  type AdminEnvironment,
} from "@/lib/deployment"

const OPTIONS: { value: AdminEnvironment; label: string }[] = [
  { value: "production", label: "Production" },
  { value: "staging", label: "Staging" },
]

function BackendBuildChip() {
  const { data, isError, isLoading } = useQuery({
    queryKey: ["backend-build"],
    queryFn: getBackendBuild,
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  })

  if (isLoading) {
    return <span className="hidden font-mono text-[11px] text-muted-foreground md:inline">backend ...</span>
  }
  if (isError || !data) {
    return <span className="hidden font-mono text-[11px] text-destructive md:inline">backend unreachable</span>
  }

  const sha = data.git_sha ? data.git_sha.slice(0, 7) : "no sha"
  const mismatch = data.environment !== "local" && data.environment !== CURRENT_ENVIRONMENT
  return (
    <span
      className={cn(
        "hidden font-mono text-[11px] md:inline",
        mismatch ? "font-semibold text-destructive" : "text-muted-foreground",
      )}
      title={data.git_sha ? `Backend ${data.environment} at ${data.git_sha}` : `Backend ${data.environment}`}
    >
      backend {data.environment} · {sha}
    </span>
  )
}

export function EnvironmentSwitch() {
  const location = useLocation()

  return (
    <div className="flex items-center gap-2">
      {CURRENT_ENVIRONMENT === "staging" && (
        <span className="rounded bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold tracking-wider text-white">
          STAGING
        </span>
      )}
      <nav
        aria-label="Admin environment"
        className="inline-flex items-center rounded-md border bg-muted p-0.5 text-xs font-medium"
      >
        {OPTIONS.map((option) => {
          const active = option.value === CURRENT_ENVIRONMENT
          const activeTone =
            option.value === "staging" ? "bg-amber-500 text-white" : "bg-background text-foreground shadow-sm"
          return active ? (
            <span key={option.value} aria-current="page" className={cn("rounded px-2 py-1", activeTone)}>
              {option.label}
            </span>
          ) : (
            <a
              key={option.value}
              href={environmentUrl(option.value, location.pathname, location.search, location.hash)}
              className="rounded px-2 py-1 text-muted-foreground transition-colors hover:text-foreground"
            >
              {option.label}
            </a>
          )
        })}
      </nav>
      <BackendBuildChip />
    </div>
  )
}
